import * as THREE from 'three';

const GLSL_NOISE = /* glsl */ `
float hash12(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float vnoise(vec2 p){
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash12(i);
  float b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0));
  float d = hash12(i + vec2(1.0, 1.0));
  return a + (b - a) * u.x + (c - a) * u.y + (a - b - c + d) * u.x * u.y;
}
float fbm(vec2 p){
  float sum = 0.0; float amp = 0.5; float tot = 0.0;
  for (int i = 0; i < 5; i++){ sum += vnoise(p) * amp; tot += amp; amp *= 0.5; p = p * 2.02 + vec2(19.7, 7.3); }
  return sum / tot;
}
`;

// Gradient sky dome with fbm clouds, sun disc + halo. One draw call.
export function buildSkyMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      uTop: { value: new THREE.Color('#1a2a5e') },
      uHorizon: { value: new THREE.Color('#ffb367') },
      uSunColor: { value: new THREE.Color('#ffd98a') },
      uSunDir: { value: new THREE.Vector3(0.42, 0.13, 0.9).normalize() },
      uCloudOffset: { value: new THREE.Vector2(0, 0) },
    },
    vertexShader: /* glsl */ `
varying vec3 vDir;
void main(){
  vDir = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`,
    fragmentShader: /* glsl */ `
varying vec3 vDir;
uniform vec3 uTop; uniform vec3 uHorizon; uniform vec3 uSunColor; uniform vec3 uSunDir; uniform vec2 uCloudOffset;
${GLSL_NOISE}
void main(){
  float h = clamp(vDir.y * 0.5 + 0.5, 0.0, 1.0);
  vec3 col = mix(uHorizon, uTop, pow(h, 0.62));
  vec2 sp = vDir.xz / max(abs(vDir.y) + 0.12, 0.12);
  float cloud = fbm(sp * 1.6 + uCloudOffset);
  cloud = smoothstep(0.52, 0.78, cloud) * smoothstep(1.05, 0.25, h);
  col = mix(col, vec3(1.02, 0.99, 0.96), cloud * 0.85);
  float d = clamp(dot(normalize(vDir), normalize(uSunDir)), 0.0, 1.0);
  col += uSunColor * (pow(d, 700.0) * 1.35 + pow(d, 4.0) * 0.3 + pow(d, 16.0) * 0.12);
  gl_FragColor = vec4(col, 1.0);
}
`,
  });
}

// Hero ocean surface: gerstner-style summed sines + fbm detail, fresnel sky
// reflection, sun specular, foam near shore depth, subsurface tint.
export function buildOceanMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uTimeF: { value: 0 },
      uSunDir: { value: new THREE.Vector3(0.42, 0.13, 0.9).normalize() },
      uSunColor: { value: new THREE.Color('#ffc46b') },
      uSkyTop: { value: new THREE.Color('#22335f') },
      uSkyHorizon: { value: new THREE.Color('#e8965a') },
      uDeep: { value: new THREE.Color('#06344d') },
      uShallow: { value: new THREE.Color('#0f6f7d') },
      uFoam: { value: new THREE.Color('#eaf6ff') },
      uCamPos: { value: new THREE.Vector3() },
      uBoatX: { value: 0 },
      uBoatZ: { value: 0 },
      uWakeStrength: { value: 0 },
      uFogColor: { value: new THREE.Color('#e8965a') },
      uFogNear: { value: 240 },
      uFogFar: { value: 395 },
    },
    vertexShader: /* glsl */ `
uniform float uTime;
uniform float uBoatX;
uniform float uBoatZ;
uniform float uWakeStrength;
varying vec3 vWorldPos;
varying vec3 vNormalW;
varying float vFoamMask;
varying float vWake;
${GLSL_NOISE}

vec3 gerstner(vec2 p, float t, float steep, float wl, vec2 dir, inout vec3 tangent, inout vec3 binormal){
  float k = 6.28318 / wl;
  float c = sqrt(9.8 / k);
  vec2 d = normalize(dir);
  float f = k * (dot(d, p) - c * t);
  float a = steep / k;
  tangent += vec3(-d.x * d.x * steep * sin(f), d.x * steep * cos(f), -d.x * d.y * steep * sin(f));
  binormal += vec3(-d.x * d.y * steep * sin(f), d.y * steep * cos(f), -d.y * d.y * steep * sin(f));
  return vec3(d.x * a * cos(f), a * sin(f), d.y * a * cos(f));
}

void main(){
  vec3 world = (modelMatrix * vec4(position, 1.0)).xyz;
  vec2 p = world.xz;

  vec3 tangent = vec3(1.0, 0.0, 0.0);
  vec3 binormal = vec3(0.0, 0.0, 1.0);
  vec3 disp = vec3(0.0);
  disp += gerstner(p, uTime, 0.3, 34.0, vec2(1.0, 0.3), tangent, binormal);
  disp += gerstner(p, uTime, 0.22, 17.0, vec2(-0.7, 0.6), tangent, binormal);
  disp += gerstner(p, uTime, 0.12, 9.0, vec2(0.4, -0.9), tangent, binormal);
  disp += gerstner(p, uTime, 0.08, 5.0, vec2(0.9, 0.8), tangent, binormal);
  disp += gerstner(p, uTime, 0.06, 3.0, vec2(-0.8, -0.5), tangent, binormal);
  disp += gerstner(p, uTime, 0.42, 60.0, vec2(0.15, 0.99), tangent, binormal);
  // Fine chop now resolves at 2u tessellation (was aliased away at 11u)
  disp.y += fbm(p * 1.4 + uTime * 0.5) * 0.12 - 0.06;
  disp.y += fbm(p * 0.35 + uTime * 0.22) * 0.06 - 0.03;

  // Boat wake: radial ring expanding from boat position.
  float bd = length(p - vec2(uBoatX, uBoatZ));
  float ring = sin(bd * 2.6 - uTime * 8.0) * 0.5 + 0.5;
  float wake = smoothstep(10.0, 1.5, bd) * ring * uWakeStrength;

  world += disp;
  world.y += wake * 0.16;

  vec3 n = normalize(cross(binormal, tangent));
  vNormalW = n;
  vWorldPos = world;

  float crest = smoothstep(0.28, 0.62, disp.y + wake * 0.5);
  vFoamMask = crest;
  vWake = wake;

  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
}
`,
    fragmentShader: /* glsl */ `
uniform vec3 uSunDir; uniform vec3 uSunColor;
uniform vec3 uSkyTop; uniform vec3 uSkyHorizon;
uniform vec3 uDeep; uniform vec3 uShallow; uniform vec3 uFoam;
uniform vec3 uCamPos;
uniform float uTimeF;
uniform float uWake;
uniform vec3 uFogColor; uniform float uFogNear; uniform float uFogFar;
varying vec3 vWorldPos;
varying vec3 vNormalW;
varying float vFoamMask;
varying float vWake;
${GLSL_NOISE}

void main(){
  vec3 viewDir = normalize(uCamPos - vWorldPos);
  vec3 n = normalize(vNormalW);
  // micro normal perturb for sparkle
  vec2 sp = vWorldPos.xz;
  n = normalize(n + vec3(
    (fbm(sp * 2.3 + vec2(uTimeF, 0.0)) - 0.5) * 0.22,
    0.0,
    (fbm(sp * 2.3 + 7.7 + vec2(uTimeF, 3.1)) - 0.5) * 0.22
  ));

  float fres = pow(1.0 - clamp(dot(n, viewDir), 0.0, 1.0), 3.0);

  vec3 sky = mix(uSkyHorizon, uSkyTop, clamp(reflect(-viewDir, n).y * 0.5 + 0.5, 0.0, 1.0));
  vec3 water = mix(uDeep, uShallow, clamp(vWorldPos.y * 1.4 + 0.35, 0.0, 1.0));

  vec3 h = normalize(uSunDir + viewDir);
  float spec = pow(clamp(dot(n, h), 0.0, 1.0), 320.0);
  float sunGlint = pow(clamp(dot(n, h), 0.0, 1.0), 48.0) * 0.35;

  vec3 col = water;
  col = mix(col, sky, fres * 0.62);
  float pathMask = smoothstep(0.82, 0.985, dot(normalize(uSunDir.xz), -normalize(viewDir.xz)));
  col += uSunColor * (spec * 2.6 * pathMask + sunGlint * 1.4 * (0.35 + 0.65 * pathMask));

  float foam = vFoamMask;
  float wakeFoam = smoothstep(0.25, 0.75, vWake) * 0.85;
  col = mix(col, uFoam, clamp(foam * 0.6 + wakeFoam, 0.0, 0.92));

  float dist = length(uCamPos - vWorldPos);
  float fog = smoothstep(uFogNear, uFogFar, dist);
  col = mix(col, uFogColor, fog * 0.85);

  gl_FragColor = vec4(col, 1.0);
}
`,
  });
}
